import React from "react";
import {
    createStyles,
    Theme,
    withStyles,
    WithStyles
} from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import SentimentVeryDissatisfiedIcon from "@material-ui/icons/SentimentVeryDissatisfied";
import SentimentDissatisfiedIcon from "@material-ui/icons/SentimentDissatisfied";
import SentimentSatisfiedIcon from "@material-ui/icons/SentimentSatisfied";
import SentimentSatisfiedAltIcon from "@material-ui/icons/SentimentSatisfiedAlt";
import SentimentVerySatisfiedIcon from "@material-ui/icons/SentimentVerySatisfied";
import { TextField, Grid, Link } from "@material-ui/core";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import { apiPost } from "../../services/api";
import { useSnackbar } from "notistack";

const styles = (theme: Theme) =>
    createStyles({
        root: {
            margin: 0,
            padding: theme.spacing(2)
        },
        closeButton: {
            position: "absolute",
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500]
        }
    });

export interface DialogTitleProps extends WithStyles<typeof styles> {
    id: string;
    children: React.ReactNode;
    onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h4">{children}</Typography>
            {onClose ? (
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(2)
    }
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(1)
    }
}))(MuiDialogActions);

const defaultRating = 3;
const defaultExperience = "";

export default function CustomizedDialogs() {
    const [open, setOpen] = React.useState(false);
    const [rating, setRating] = React.useState(defaultRating);
    const [experience, setExperience] = React.useState(defaultExperience);

    const { enqueueSnackbar } = useSnackbar();

    const resetForm = () => {
        setRating(defaultRating);
        setExperience(defaultExperience);
    };

    const handleClickOpen = () => {
        resetForm();
        setOpen(true);
    };

    const handleSend = async () => {
        setOpen(false);

        enqueueSnackbar(`Thank you for your feedback.`, {
            variant: "success"
        });

        await apiPost(`users/feedback`, {
            rating: `${rating}`,
            experience: experience
        });
    };

    const handleClose = async () => {
        setOpen(false);
    };

    const handleExperienceChanged = e => {
        setExperience(e.target.value);
    };

    const handleChange = (event, newAlignment) => {
        if (event != null) {
        }
        setRating(newAlignment);
    };

    const children = [
        <ToggleButton key={1} value={1}>
            <SentimentVeryDissatisfiedIcon fontSize="large" color="primary" />
        </ToggleButton>,
        <ToggleButton key={2} value={2}>
            <SentimentDissatisfiedIcon fontSize="large" color="primary" />
        </ToggleButton>,
        <ToggleButton key={3} value={3}>
            <SentimentSatisfiedIcon fontSize="large" color="primary" />
        </ToggleButton>,
        <ToggleButton key={4} value={4}>
            <SentimentSatisfiedAltIcon fontSize="large" color="primary" />
        </ToggleButton>,
        <ToggleButton key={5} value={5}>
            <SentimentVerySatisfiedIcon fontSize="large" color="primary" />
        </ToggleButton>
    ];

    return (
        <>
            <Link
                color="inherit"
                href="#"
                underline="always"
                onClick={handleClickOpen}
            >
                feedback
            </Link>
            <Dialog
                fullWidth={true}
                maxWidth={"sm"}
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <DialogTitle id="customized-dialog-title" onClose={handleClose}>
                    How was your experience?
                </DialogTitle>
                <DialogContent dividers>
                    <Grid
                        container
                        spacing={2}
                        direction="column"
                        style={{ paddingBottom: "20px", paddingTop: "10px" }}
                    >
                        <Grid item>
                            <ToggleButtonGroup
                                size="medium"
                                value={rating}
                                exclusive
                                onChange={handleChange}
                            >
                                {children}
                            </ToggleButtonGroup>
                        </Grid>
                    </Grid>
                    <TextField
                        fullWidth
                        id="outlined-multiline-static"
                        label="Summarise your feedback"
                        multiline
                        rows={4}
                        placeholder="Type here..."
                        onChange={handleExperienceChanged}
                        variant="outlined"
                    />

                    <DialogActions style={{ paddingTop: "15px" }}>
                        <Button
                            autoFocus
                            onClick={handleSend}
                            variant="contained"
                            color="primary"
                            style={{ padding: "10px" }}
                        >
                            Send Feedback
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </>
    );
}
